import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import {
  PhChatCircleDots,
  PhCheckCircle,
  PhArrowCounterClockwise,
  PhPaperPlaneTilt,
  PhUser,
  PhCaretDown,
  PhCaretRight,
} from 'ember-phosphor-icons';
import Drawer from 'tio-ui/components/drawer';
import type ComponentReviewService from 'tio-employee/services/component-review';
import type { ComponentReviewThreadExtension as ComponentReviewThreadModel } from 'tio-data/data/resources/component-review-thread.ext';
import type { ComponentReviewComment as ComponentReviewCommentModel } from 'tio-data/data/resources/component-review-comment.type';

// ─── Helper: format relative time ──────────────────────────────────────────

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(isoString).toLocaleDateString();
}

// ─── Sub-component: Single Comment ─────────────────────────────────────────

interface CommentBubbleSignature {
  Args: {
    comment: ComponentReviewCommentModel;
  };
  Element: HTMLDivElement;
}

class CommentBubble extends Component<CommentBubbleSignature> {
  get timeAgo() {
    return formatRelativeTime(this.args.comment.createdAt);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="tio-flex group" ...attributes>
      <div class="flex-none w-8 h-8 rounded-full tio-bg-brand-soft tio-row-center">
        <PhUser @size={{14}} class="tio-fg-brand" />
      </div>
      <div class="tio-fill-min">
        <div class="tio-flex-tight items-baseline">
          <span class="tio-text-sm-medium tio-fg-primary">
            {{@comment.author.fullName}}
          </span>
          <span class="text-xs tio-fg-muted">
            {{this.timeAgo}}
          </span>
        </div>
        <div class="mt-1 text-sm tio-fg-secondary whitespace-pre-wrap">
          {{@comment.body}}
        </div>
      </div>
    </div>
  </template>
}

// ─── Sub-component: Thread Card ────────────────────────────────────────────

interface ThreadCardSignature {
  Args: {
    thread: ComponentReviewThreadModel;
    onResolve: (threadId: string) => void;
    onReopen: (threadId: string) => void;
    onAddComment: (threadId: string, body: string) => void;
  };
  Element: HTMLDivElement;
}

class ThreadCard extends Component<ThreadCardSignature> {
  // eslint-disable-next-line ember/no-tracked-properties-from-args -- intentional editable local copy: seeded from args then toggled by the user (line 107)
  @tracked isExpanded = this.args.thread.isOpen;
  @tracked newCommentText = '';

  get isClosed() {
    return this.args.thread.isResolved || this.args.thread.status === 'wontfix';
  }

  get threadAge() {
    return formatRelativeTime(this.args.thread.createdAt);
  }

  get commentCount() {
    return this.args.thread.commentCount;
  }

  get hasMultipleComments() {
    return this.commentCount !== 1;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  handleResolve = async () => {
    const id = this.args.thread.id;
    if (!id) return;
    await this.args.onResolve(id);
  };

  handleReopen = async () => {
    const id = this.args.thread.id;
    if (!id) return;
    await this.args.onReopen(id);
  };

  submitComment = (event: Event) => {
    event.preventDefault();
    const text = this.newCommentText.trim();
    const id = this.args.thread.id;
    if (!text || !id) return;
    this.args.onAddComment(id, text);
    this.newCommentText = '';
  };

  updateCommentText = (event: Event) => {
    this.newCommentText = (event.target as HTMLTextAreaElement).value;
  };

  get isSubmitDisabled() {
    return this.newCommentText.trim().length === 0;
  }

  handleKeyDown = (event: KeyboardEvent) => {
    // Cmd/Ctrl+Enter to submit
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.submitComment(event);
    }
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="border rounded-lg overflow-hidden
        {{if
          this.isClosed
          'tio-border-success tio-bg-success-soft'
          'tio-border-brand-soft tio-bg-surface'
        }}"
      ...attributes
    >
      {{! Thread header - clickable to expand/collapse }}
      <button
        type="button"
        class="w-full tio-row-tight px-4 py-3 text-left hover:tio-bg-sunken transition-colors"
        {{on "click" this.toggleExpanded}}
      >
        {{#if this.isExpanded}}
          <PhCaretDown @size={{14}} class="tio-fg-muted flex-none" />
        {{else}}
          <PhCaretRight @size={{14}} class="tio-fg-muted flex-none" />
        {{/if}}

        <div class="tio-fill-min">
          <div class="tio-row-tight">
            {{#if this.isClosed}}
              <PhCheckCircle @size={{16}} @weight="fill" class="tio-fg-success flex-none" />
              <span class="tio-text-sm-medium tio-fg-success-body">
                {{if (eq @thread.status "wontfix") "Won't Fix" "Resolved"}}
              </span>
            {{else}}
              <PhChatCircleDots @size={{16}} class="tio-fg-brand flex-none" />
              <span class="tio-text-sm-medium tio-fg-primary">
                Open
              </span>
            {{/if}}
            <span class="text-xs tio-fg-muted">
              ·
              {{this.commentCount}}
              comment{{if this.hasMultipleComments "s" ""}}
              ·
              {{this.threadAge}}
            </span>
          </div>
        </div>
      </button>

      {{! Thread body - comments + input }}
      {{#if this.isExpanded}}
        <div class="border-t tio-border-subtle">
          {{! Screenshot (if captured) }}
          {{#if @thread.screenshotUrl}}
            <div class="p-3 border-b tio-border-subtle">
              <img
                src={{@thread.screenshotUrl}}
                alt="Component screenshot"
                class="w-full rounded-lg border tio-border-default shadow-sm"
              />
            </div>
          {{/if}}

          {{! Comments list }}
          <div class="p-4 space-y-4">
            {{#each @thread.comments as |comment|}}
              <CommentBubble @comment={{comment}} />
            {{/each}}
          </div>

          {{! Actions bar }}
          <div class="px-4 py-2 border-t tio-border-subtle tio-row-tight">
            {{#if this.isClosed}}
              <button
                type="button"
                class="tio-row-extra-tight px-3 py-1.5 tio-text-xs-medium tio-fg-warning-body tio-bg-warning-soft rounded-lg hover:tio-bg-warning-soft-hover transition-colors"
                {{on "click" this.handleReopen}}
              >
                <PhArrowCounterClockwise @size={{12}} />
                Reopen
              </button>
            {{else}}
              <button
                type="button"
                class="tio-row-extra-tight px-3 py-1.5 tio-text-xs-medium tio-fg-success-body tio-bg-success-soft rounded-lg hover:tio-bg-success-soft-hover transition-colors"
                {{on "click" this.handleResolve}}
              >
                <PhCheckCircle @size={{12}} />
                Resolve
              </button>
            {{/if}}
          </div>

          {{! New comment input }}
          {{#unless this.isClosed}}
            <form
              class="p-3 border-t tio-border-subtle tio-bg-sunken"
              {{on "submit" this.submitComment}}
            >
              <textarea
                class="tio-review-drawer-field w-full px-3 py-2 text-sm border tio-border-default rounded-lg tio-bg-surface tio-fg-primary placeholder:tio-fg-muted focus:ring-2 focus:tio-ring-brand resize-none"
                rows="2"
                placeholder="Add a comment... (⌘+Enter to send)"
                value={{this.newCommentText}}
                {{on "input" this.updateCommentText}}
                {{on "keydown" this.handleKeyDown}}
              ></textarea>
              <div class="tio-flex-end mt-2">
                <button
                  type="submit"
                  class="tio-row-extra-tight px-3 py-1.5 tio-text-xs-medium tio-fg-on-brand tio-bg-brand rounded-lg hover:tio-bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={{this.isSubmitDisabled}}
                >
                  <PhPaperPlaneTilt @size={{12}} />
                  Send
                </button>
              </div>
            </form>
          {{/unless}}
        </div>
      {{/if}}
    </div>
  </template>
}

// ─── Main ReviewDrawer ─────────────────────────────────────────────────────

interface ReviewDrawerSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    reviewId?: string;
    pagePath?: string;
    label?: string;
  };
  Element: HTMLDivElement;
}

/**
 * ReviewDrawer Component
 *
 * Slide-out drawer that shows all review threads for a specific component.
 * Allows creating new threads, adding comments, and resolving/reopening threads.
 *
 * Uses the same Drawer shell as DataSourceDocs for consistency.
 */
export default class ReviewDrawer extends Component<ReviewDrawerSignature> {
  @service declare componentReview: ComponentReviewService;

  @tracked newThreadComment = '';
  @tracked isCreatingThread = false;
  @tracked filter: 'all' | 'open' | 'resolved' = 'all';

  get threads(): ComponentReviewThreadModel[] {
    if (!this.args.reviewId) return [];
    const all = this.componentReview.getThreadsForComponent(this.args.reviewId, this.args.pagePath);

    switch (this.filter) {
      case 'open':
        return all.filter((t) => t.status === 'open');
      case 'resolved':
        return all.filter((t) => t.status === 'resolved');
      default:
        return all;
    }
  }

  get hasThreads() {
    return this.threads.length > 0;
  }

  get displayLabel() {
    return this.args.label ?? this.args.reviewId ?? 'Component';
  }

  get openCount() {
    if (!this.args.reviewId) return 0;
    return this.componentReview.getOpenThreadCount(this.args.reviewId, this.args.pagePath);
  }

  // ── New thread form ──────────────────────────────────────────────────

  startNewThread = () => {
    this.isCreatingThread = true;
  };

  cancelNewThread = () => {
    this.isCreatingThread = false;
    this.newThreadComment = '';
  };

  updateNewThreadComment = (event: Event) => {
    this.newThreadComment = (event.target as HTMLTextAreaElement).value;
  };

  submitNewThread = async (event: Event) => {
    event.preventDefault();
    const text = this.newThreadComment.trim();
    if (!text || !this.args.reviewId) return;

    // Capture screenshot from the component's DOM element (CSS.escape for safety)
    const escapedId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(this.args.reviewId)
        : this.args.reviewId;
    const element = document.querySelector(`[data-review-id="${escapedId}"]`) as HTMLElement | null;
    const screenshot = element ? await this.componentReview.captureScreenshot(element) : undefined;

    await this.componentReview.createThread(this.args.reviewId, text, screenshot);
    this.newThreadComment = '';
    this.isCreatingThread = false;
  };

  handleNewThreadKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.submitNewThread(event);
    }
    if (event.key === 'Escape') {
      this.cancelNewThread();
    }
  };

  // ── Thread actions ────────────────────────────────────────────────────

  handleResolve = async (threadId: string) => {
    await this.componentReview.resolveThread(threadId);
  };

  handleReopen = async (threadId: string) => {
    await this.componentReview.reopenThread(threadId);
  };

  handleAddComment = async (threadId: string, body: string) => {
    await this.componentReview.addComment(threadId, body);
  };

  // ── Filter ────────────────────────────────────────────────────────────

  setFilterAll = () => {
    this.filter = 'all';
  };

  setFilterOpen = () => {
    this.filter = 'open';
  };

  setFilterResolved = () => {
    this.filter = 'resolved';
  };

  get isFilterAll() {
    return this.filter === 'all';
  }

  get isFilterOpen() {
    return this.filter === 'open';
  }

  get isFilterResolved() {
    return this.filter === 'resolved';
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="lg"
      @placement="right"
      @closeOnOutsideClick={{true}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        <div class="tio-row-tight">
          <PhChatCircleDots @size={{24}} class="tio-fg-brand" />
          <div>
            <h2 class="tio-fg-primary font-semibold">
              Review:
              {{this.displayLabel}}
            </h2>
            <p class="text-xs tio-fg-muted mt-0.5">
              {{@pagePath}}
              ·
              {{this.openCount}}
              open
            </p>
          </div>
        </div>
      </:header>

      <:body>
        {{! Filter tabs }}
        <div class="tio-flex-extra-tight mb-4 p-1 tio-surface-sunken">
          <button
            type="button"
            class="flex-1 px-3 py-1.5 tio-text-xs-medium rounded-md transition-colors
              {{if
                this.isFilterAll
                'tio-bg-surface tio-fg-primary shadow-sm'
                'tio-fg-muted hover:tio-fg-secondary'
              }}"
            {{on "click" this.setFilterAll}}
          >
            All
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-1.5 tio-text-xs-medium rounded-md transition-colors
              {{if
                this.isFilterOpen
                'tio-bg-surface tio-fg-primary shadow-sm'
                'tio-fg-muted hover:tio-fg-secondary'
              }}"
            {{on "click" this.setFilterOpen}}
          >
            Open
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-1.5 tio-text-xs-medium rounded-md transition-colors
              {{if
                this.isFilterResolved
                'tio-bg-surface tio-fg-primary shadow-sm'
                'tio-fg-muted hover:tio-fg-secondary'
              }}"
            {{on "click" this.setFilterResolved}}
          >
            Resolved
          </button>
        </div>

        {{! Thread list }}
        <div class="space-y-3">
          {{#each this.threads as |thread|}}
            <ThreadCard
              @thread={{thread}}
              @onResolve={{this.handleResolve}}
              @onReopen={{this.handleReopen}}
              @onAddComment={{this.handleAddComment}}
            />
          {{/each}}

          {{#unless this.hasThreads}}
            <div class="text-center py-8">
              <PhChatCircleDots @size={{48}} class="mx-auto tio-fg-disabled mb-3" />
              <p class="text-sm tio-fg-muted">
                No threads yet for this component.
              </p>
              <p class="text-xs tio-fg-muted mt-1">
                Start one below to discuss this section.
              </p>
            </div>
          {{/unless}}
        </div>

        {{! New thread form }}
        {{#if this.isCreatingThread}}
          <form
            class="mt-4 p-4 border-2 border-dashed tio-border-brand-soft rounded-lg tio-bg-brand-wash"
            {{on "submit" this.submitNewThread}}
          >
            <label class="block tio-text-sm-medium tio-fg-secondary mb-2">
              New Review Thread
            </label>
            <textarea
              class="tio-review-drawer-field w-full px-3 py-2 text-sm border tio-border-default rounded-lg tio-bg-surface tio-fg-primary placeholder:tio-fg-muted focus:ring-2 focus:tio-ring-brand resize-none"
              rows="3"
              placeholder="What needs to be discussed about this section? (⌘+Enter to create)"
              value={{this.newThreadComment}}
              {{on "input" this.updateNewThreadComment}}
              {{on "keydown" this.handleNewThreadKeyDown}}
            ></textarea>
            <div class="tio-flex-end gap-2 mt-2">
              <button
                type="button"
                class="px-3 py-1.5 tio-text-xs-medium tio-fg-secondary hover:tio-fg-primary transition-colors"
                {{on "click" this.cancelNewThread}}
              >
                Cancel
              </button>
              <button
                type="submit"
                class="tio-row-extra-tight px-3 py-1.5 tio-text-xs-medium tio-fg-on-brand tio-bg-brand rounded-lg hover:tio-bg-brand-hover transition-colors"
              >
                <PhChatCircleDots @size={{12}} />
                Create Thread
              </button>
            </div>
          </form>
        {{/if}}
      </:body>

      <:footer>
        <div class="tio-row-between">
          <span class="text-xs tio-fg-muted">
            Threads persist across sessions · Non-production only
          </span>
          {{#unless this.isCreatingThread}}
            <button
              type="button"
              class="tio-row-extra-tight px-4 py-2 tio-text-sm-medium tio-fg-on-brand tio-bg-brand rounded-lg hover:tio-bg-brand-hover transition-colors"
              {{on "click" this.startNewThread}}
            >
              <PhChatCircleDots @size={{16}} />
              New Thread
            </button>
          {{/unless}}
        </div>
      </:footer>
    </Drawer>
  </template>
}
